import React, { useRef, useState, useEffect, useImperativeHandle } from "react";
import styled from "@emotion/styled";
import { FriendlyCaptchaSDK } from "@friendlycaptcha/sdk";

const sdk =
    typeof window !== "undefined"
        ? new FriendlyCaptchaSDK({
              apiEndpoint: process.env.GATSBY_ADMIN_SITE_URL,
              disableEvalPatching: process.env.NODE_ENV === "development",
          })
        : null;

const Widget = styled.div`
    margin-top: 10px;
    display: ${({ show }) => (show ? "block" : "none")};
`;

const FriendlyCaptcha = (props) => {
    const captchaRef = useRef(null);
    const widgetRef = useRef(null);

    const [showWidget, setShowWidget] = useState(!!props.show);

    // Separate callback props from widget options
    const { onComplete, onError, onExpire, onReset, ref, ...widgetOptions } =
        props;

    useEffect(() => {
        if (captchaRef.current && sdk) {
            widgetRef.current = sdk.createWidget({
                element: captchaRef.current,
                sitekey: process.env.GATSBY_FRIENDLY_CAPTCHA_SITE_KEY,
                ...widgetOptions,
            });

            return () => widgetRef.current?.destroy();
        }
    }, Object.values(widgetOptions)); // eslint-disable-line react-hooks/exhaustive-deps

    // Update event listeners when callbacks change
    useEffect(() => {
        const element = captchaRef.current;
        if (!element) return;

        const onStateChange = (e) => {
            if (e.detail.mode === "interactive") {
                setShowWidget(true);
            }

            if (e.detail.state === "error") {
                setShowWidget(true);
                if (onError) {
                    onError(e.detail.error);
                }
            }

            if (e.detail.state === "expired") {
                setShowWidget(true);
                if (onExpire) {
                    onExpire(e);
                }
            }

            if (e.detail.state === "reset") {
                setShowWidget(false);
                if (onReset) {
                    onReset(e);
                }
            }

            if (e.detail.state === "completed") {
                setShowWidget(false);
                if (onComplete) {
                    onComplete(e.detail.response);
                }
            }
        };

        const handleNewTabResponse = (e) => {
            if (e.origin !== window.location.origin) return;

            const { data } = e;

            if (data.downloadCaptcha) {
                if (onComplete) {
                    onComplete(data.downloadCaptcha);
                    widgetRef.current?.reset();
                }
            }
        };

        element.addEventListener("frc:widget.statechange", onStateChange);
        window.addEventListener("message", handleNewTabResponse);

        return () => {
            element.removeEventListener(
                "frc:widget.statechange",
                onStateChange,
            );
            window.removeEventListener("message", handleNewTabResponse);
        };
    }, [onComplete, onError, onExpire, onReset]);

    useImperativeHandle(ref, () => ({
        reset: () => widgetRef.current?.reset(),
        start: () => widgetRef.current?.start(),
        getResponse: () => widgetRef.current?.getResponse(),
    }));

    return <Widget ref={captchaRef} show={showWidget} />;
};

export default FriendlyCaptcha;
